// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useRef, useEffect } from 'react';

const RacerMode = ({
  backgroundImage,
  characterImage,
  enemyImage,
  gameTitle,
  isPlaying,
  onScoreUpdate,
  onGameOver,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const imagesRef = useRef({});

  useEffect(() => {
    const loadImg = (src) => new Promise(res => {
      if (!src) { res(null); return; }
      const img = new Image();
      if (src.startsWith('http')) img.crossOrigin = 'anonymous';
      img.onload = () => res(img); img.onerror = () => res(null);
      img.src = src;
    });
    Promise.all([
      loadImg(backgroundImage), loadImg(characterImage), loadImg(enemyImage),
    ]).then(([bg, ch, en]) => { imagesRef.current = { bg, ch, en }; });
  }, [backgroundImage, characterImage, enemyImage]);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;
    const horizonY = height * 0.38;

    const s = {
      frame: 0, score: 0, speed: 5,
      playerX: 0.5, steer: 0,
      roadOffset: 0,
      rivals: [], rivalTimer: 0,
      gameOver: false, lives: 3, invincible: 0,
      boost: 0, boosting: false,
    };

    const keys = {};
    const onKeyDown = (e) => { keys[e.key] = true; };
    const onKeyUp = (e) => { keys[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.gameActions = {
      moveLeft: () => { s.steer = -1; },
      moveRight: () => { s.steer = 1; },
      moveUp: () => { s.speed = Math.min(12, s.speed + 0.5); },
      moveDown: () => { s.speed = Math.max(2, s.speed - 0.5); },
      shoot: () => { if (s.boost >= 30) { s.boosting = true; s.boost -= 30; } },
    };

    const loop = () => {
      s.frame++;
      const images = imagesRef.current;
      ctx.clearRect(0, 0, width, height);

      if (s.gameOver) {
        ctx.fillStyle = '#000'; ctx.fillRect(0, 0, width, height);
        if (onGameOver) onGameOver(Math.floor(s.score));
        return;
      }

      // Input
      if (keys['ArrowLeft'] || keys['a']) s.steer = -1;
      else if (keys['ArrowRight'] || keys['d']) s.steer = 1;
      else s.steer *= 0.85;
      if (keys['ArrowUp'] || keys['w']) s.speed = Math.min(12, s.speed + 0.1);
      if (keys['ArrowDown'] || keys['s']) s.speed = Math.max(2, s.speed - 0.2);

      s.playerX += s.steer * 0.015;
      s.playerX = Math.max(0.15, Math.min(0.85, s.playerX));
      s.roadOffset += s.speed;
      s.score += s.speed * 0.15;
      s.boost = Math.min(100, s.boost + 0.1);
      if (s.boosting) { s.speed = 14; if (s.frame % 60 === 0) s.boosting = false; }
      if (s.invincible > 0) s.invincible--;
      if (onScoreUpdate) onScoreUpdate(Math.floor(s.score));

      // Spawn rivals
      s.rivalTimer++;
      if (s.rivalTimer >= 80) {
        s.rivalTimer = 0;
        s.rivals.push({
          x: 0.2 + Math.random() * 0.6,
          z: 1.0,
          speed: 0.008 + Math.random() * 0.005,
          frame: 0,
        });
      }

      // Update rivals
      s.rivals.forEach(r => { r.z -= r.speed; r.frame++; });
      s.rivals = s.rivals.filter(r => r.z > -0.3);

      // Collision
      if (s.invincible <= 0) {
        for (let r of s.rivals) {
          if (r.z < 0.15 && r.z > 0) {
            if (Math.abs(r.x - s.playerX) < 0.1) {
              s.lives--;
              s.invincible = 60;
              if (s.lives <= 0) s.gameOver = true;
              s.speed = Math.max(3, s.speed - 2);
            }
          }
        }
      }

      // === DRAW ===
      // Sky
      if (images.bg) {
        ctx.drawImage(images.bg, 0, 0, width, horizonY + 20);
      } else {
        const skyGrad = ctx.createLinearGradient(0, 0, 0, horizonY);
        skyGrad.addColorStop(0, '#87CEEB'); skyGrad.addColorStop(1, '#E0F0FF');
        ctx.fillStyle = skyGrad; ctx.fillRect(0, 0, width, horizonY);
      }

      // Road (pseudo-3D perspective)
      for (let y = horizonY; y < height; y++) {
        const progress = (y - horizonY) / (height - horizonY);
        const perspective = progress * progress;
        const roadWidth = perspective * width * 0.7;
        const cx = width / 2 + s.steer * perspective * -60;
        const leftEdge = cx - roadWidth / 2;
        const rightEdge = cx + roadWidth / 2;

        // Road surface
        const segIndex = Math.floor((s.roadOffset * 0.3 + y) * 0.08);
        ctx.fillStyle = segIndex % 2 === 0 ? '#444' : '#3a3a3a';
        ctx.fillRect(leftEdge, y, roadWidth, 1);

        // Road markings
        const laneW = roadWidth / 3;
        if (segIndex % 4 < 2) {
          ctx.fillStyle = '#fff';
          ctx.fillRect(leftEdge + laneW - 1, y, 2, 1);
          ctx.fillRect(leftEdge + laneW * 2 - 1, y, 2, 1);
        }

        // Edge lines
        ctx.fillStyle = segIndex % 2 === 0 ? '#f00' : '#fff';
        ctx.fillRect(leftEdge - 3, y, 3, 1);
        ctx.fillRect(rightEdge, y, 3, 1);

        // Grass
        const grassColor = segIndex % 2 === 0 ? '#2a8a2a' : '#25a025';
        ctx.fillStyle = grassColor;
        ctx.fillRect(0, y, leftEdge - 3, 1);
        ctx.fillRect(rightEdge + 3, y, width - rightEdge - 3, 1);
      }

      // Draw rivals (3D perspective)
      s.rivals.forEach(r => {
        const scale = Math.max(0.1, 1 - r.z);
        const screenX = width / 2 + (r.x - 0.5) * width * 0.7 * scale;
        const screenY = horizonY + (1 - r.z) * (height - horizonY) * scale;
        const carW = 60 * scale;
        const carH = 50 * scale;
        if (images.en) {
          ctx.save();
          ctx.translate(screenX, screenY);
          const wobble = Math.sin(r.frame * 0.05) * 0.03;
          ctx.rotate(wobble);
          ctx.drawImage(images.en, -carW / 2, -carH, carW, carH);
          ctx.restore();
        } else {
          ctx.fillStyle = '#f44';
          ctx.fillRect(screenX - carW / 2, screenY - carH, carW, carH);
        }
      });

      // Draw player car (bottom center)
      const carW = 90;
      const carH = 70;
      const px = s.playerX * width;
      const py = height - 30;
      const blink = s.invincible > 0 && Math.floor(s.frame / 4) % 2 === 0;
      if (!blink) {
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(-s.steer * 0.08);
        if (images.ch) {
          const bounce = Math.sin(s.frame * 0.2) * 2;
          ctx.drawImage(images.ch, -carW / 2, -carH + bounce, carW, carH);
        } else {
          ctx.fillStyle = '#0088FF';
          ctx.fillRect(-carW / 2, -carH, carW, carH);
        }
        // Engine flames
        if (s.boosting || s.speed > 8) {
          ctx.fillStyle = `rgba(255,${100 + Math.random() * 100},0,0.7)`;
          ctx.fillRect(-8, -5, 16, 10 + Math.random() * 10);
        }
        ctx.restore();
      }

      // Dashboard HUD
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, height - 45, width, 45);
      ctx.fillStyle = '#0f0'; ctx.font = 'bold 16px Rajdhani, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`${Math.floor(s.speed * 20)} MPH`, 15, height - 18);
      ctx.fillStyle = '#fff'; ctx.textAlign = 'center';
      ctx.fillText(`${Math.floor(s.score)}m`, width / 2, height - 18);
      // Lives
      ctx.textAlign = 'right';
      for (let i = 0; i < s.lives; i++) { ctx.fillStyle = '#f00'; ctx.fillText('♥', width - 15 - i * 20, height - 18); }
      // Boost bar
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fillRect(width / 2 - 50, height - 40, 100, 6);
      ctx.fillStyle = s.boost >= 30 ? '#0ff' : '#666';
      ctx.fillRect(width / 2 - 50, height - 40, s.boost, 6);

      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      delete window.gameActions;
    };
  }, [isPlaying, onScoreUpdate, onGameOver]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="racer-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: 'none' }}
    />
  );
};

export default RacerMode;
